import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays, startOfDay } from "date-fns";

export interface DueAlert {
  id: string;
  description: string;
  amount: number;
  transaction_date: string;
  type: "income" | "expense";
  daysUntilDue: number;
  urgency: "overdue" | "today" | "soon" | "upcoming";
  categoryName?: string;
  categoryColor?: string;
  account_id: string;
  category_id: string | null;
  is_paid: boolean;
  is_recurring: boolean;
  is_fixed: boolean;
}

export interface DueAlertsSummary {
  overdue: DueAlert[];
  dueToday: DueAlert[];
  dueSoon: DueAlert[]; // Next 3 days
  upcoming: DueAlert[]; // 4-7 days
  totalOverdue: number;
  totalDueToday: number;
}

export const useDueAlerts = () => {
  const [alerts, setAlerts] = useState<DueAlertsSummary>({
    overdue: [],
    dueToday: [],
    dueSoon: [],
    upcoming: [],
    totalOverdue: 0,
    totalDueToday: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchAlerts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = startOfDay(new Date());
      
      // Get unpaid transactions within 7 days or overdue
      // RLS policies handle access control for shared accounts
      const { data: transactions, error } = await supabase
        .from("transactions")
        .select(`
          *,
          categories(name, color),
          accounts!inner(type)
        `)
        .eq("is_paid", false)
        .neq("accounts.type", "credit_card")
        .lte("transaction_date", new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0])
        .order("transaction_date", { ascending: true });

      if (error) throw error;

      const categorizedAlerts: DueAlertsSummary = {
        overdue: [],
        dueToday: [],
        dueSoon: [],
        upcoming: [],
        totalOverdue: 0,
        totalDueToday: 0,
      };

      for (const tx of transactions || []) {
        const txDate = startOfDay(new Date(tx.transaction_date));
        const daysUntilDue = differenceInDays(txDate, today);

        let urgency: DueAlert["urgency"];
        if (daysUntilDue < 0) {
          urgency = "overdue";
        } else if (daysUntilDue === 0) {
          urgency = "today";
        } else if (daysUntilDue <= 3) {
          urgency = "soon";
        } else {
          urgency = "upcoming";
        }

        const alert: DueAlert = {
          id: tx.id,
          description: tx.description,
          amount: Number(tx.amount),
          transaction_date: tx.transaction_date,
          type: tx.type,
          daysUntilDue,
          urgency,
          categoryName: tx.categories?.name,
          categoryColor: tx.categories?.color,
          account_id: tx.account_id,
          category_id: tx.category_id,
          is_paid: tx.is_paid,
          is_recurring: tx.is_recurring,
          is_fixed: tx.is_fixed,
        };

        switch (urgency) {
          case "overdue":
            categorizedAlerts.overdue.push(alert);
            categorizedAlerts.totalOverdue += Number(tx.amount);
            break;
          case "today":
            categorizedAlerts.dueToday.push(alert);
            categorizedAlerts.totalDueToday += Number(tx.amount);
            break;
          case "soon":
            categorizedAlerts.dueSoon.push(alert);
            break;
          case "upcoming":
            categorizedAlerts.upcoming.push(alert);
            break;
        }
      }

      setAlerts(categorizedAlerts);
    } catch (error) {
      console.error("Error fetching due alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async (transactionId: string) => {
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ is_paid: true })
        .eq("id", transactionId);

      if (error) throw error;
      await fetchAlerts();
      return true;
    } catch (error) {
      console.error("Error marking as paid:", error);
      return false;
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  return {
    alerts,
    loading,
    refetch: fetchAlerts,
    markAsPaid,
  };
};
